/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { POSTFACH_NACHRICHT_UPLOADED_ATTACHMENTS } from '@administration-client/postfach-shared';
import {
  CommandBasedService,
  CommandListResourceServiceConfig,
  CommandResource,
  CommandService,
  doIfCommandIsDone,
  isDone,
  isPending,
  tapOnCommandSuccessfullyDone,
} from '@alfa-client/command-shared';
import { NavigationFacade, NavigationService, RouteData } from '@alfa-client/navigation-shared';
import {
  VorgangHeaderLinkRel,
  VorgangNavigationUtil,
  VorgangService,
  VorgangWithEingangResource,
} from '@alfa-client/vorgang-shared';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Params } from '@angular/router';
import { BinaryFileActions, BinaryFileService, DownloadBinaryFileAsPdfAction } from '@common/binary-file-shared';
import {
  ApiError,
  ApiErrorAction,
  createLoadingStateResource,
  formatFullDateWithoutSeperator,
  isLoaded,
  isNotUndefined,
  ResourceServiceConfig,
  StateResource,
} from '@common/tech-shared';
import { Action, Store } from '@ngrx/store';
import { getUrl, hasLink, Resource } from '@ngxp/rest';
import { isNil } from 'lodash-es';
import { BehaviorSubject, combineLatest, filter, Observable, of, startWith, Subscription } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { PostfachActions } from './+state/postfach.actions';
import { PostfachSelectors } from './+state/postfach.selectors';
import { PostfachMailLinkRel, PostfachMailListLinkRel } from './postfach.linkrel';
import { PostfachMessages } from './postfach.message';
import {
  CreatePostfachMailCommand,
  PostfachFeatures,
  PostfachMail,
  PostfachMailListResource,
  PostfachMailResource,
  PostfachSettings,
} from './postfach.model';
import { PostfachRepository } from './postfach.repository';
import { createResendPostfachMailCommand, createSendPostfachMailCommand } from './postfach.util';

@Injectable({ providedIn: 'root' })
export class PostfachService extends CommandBasedService<
  PostfachMailResource,
  PostfachMailResource,
  VorgangWithEingangResource,
  PostfachMailListResource,
  PostfachMailResource,
  PostfachMail
> {
  private readonly repository = inject(PostfachRepository);
  private readonly commandService = inject(CommandService);
  private readonly navigationService = inject(NavigationService);
  private readonly vorgangService = inject(VorgangService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly binaryFileService = inject(BinaryFileService);
  private readonly navigationFacade = inject(NavigationFacade);

  private readonly isPollSendPostachMail: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private navigationSubscription: Subscription;

  private sendPostfachMailSubscription: Subscription;

  constructor() {
    super();
    this._listenToNavigation();
  }

  getListResourceConfig(): CommandListResourceServiceConfig<VorgangWithEingangResource> {
    const vorgangService = inject(VorgangService);
    return {
      baseResource: vorgangService.getVorgangWithEingang(),
      getLinkRel: VorgangHeaderLinkRel.POSTFACH_MAILS,
      listResourceListLinkRel: PostfachMailListLinkRel.POSTFACH_MAIL_LIST,
    };
  }

  getResourceConfig(): ResourceServiceConfig<PostfachMailResource> {
    return undefined;
  }

  getFeatureName(): string {
    return 'PostfachMail';
  }

  public sendMail(postfachMail: PostfachMail): Observable<StateResource<CommandResource>> {
    return this.getList().pipe(
      filter(isLoaded),
      take(1),
      switchMap((postfachMailList) =>
        this._doSendNachricht(
          postfachMailList.resource,
          PostfachMailListLinkRel.SEND_POSTFACH_MAIL,
          createSendPostfachMailCommand(postfachMail),
        ),
      ),
    );
  }

  public sendExistingMail(
    postfachMailResource: PostfachMailResource,
    postfachMail: PostfachMail,
  ): Observable<StateResource<CommandResource>> {
    return this._doSendNachricht(postfachMailResource, PostfachMailLinkRel.SEND, createSendPostfachMailCommand(postfachMail));
  }

  public resendMail(postfachMailResource: PostfachMailResource): Observable<StateResource<CommandResource>> {
    return this._doSendNachricht(
      postfachMailResource,
      PostfachMailLinkRel.RESEND_POSTFACH_MAIL,
      createResendPostfachMailCommand(),
    );
  }

  _doSendNachricht(
    resource: Resource,
    linkRel: string,
    command: CreatePostfachMailCommand,
  ): Observable<StateResource<CommandResource>> {
    this.vorgangService.setPendingSendPostfachMailSingleCommandLoading();

    this.commandService
      .createCommand({
        resource,
        linkRel,
        command,
        snackBarMessage: PostfachMessages.SEND_SUCCESSFUL,
        snackBarErrorMessage: PostfachMessages.SEND_FAILED,
      })
      .subscribe((createdCommand) => this._handleSendNachrichtCommand(createdCommand));

    return this.getPendingSendPostfachMailCommand();
  }

  _handleSendNachrichtCommand(commandStateResource: StateResource<CommandResource>): void {
    this.vorgangService.setPendingSendPostfachMailCommand(commandStateResource);
    this._commandIsDone(commandStateResource);
  }

  _commandIsDone(commandStateResource: StateResource<CommandResource>): void {
    doIfCommandIsDone(commandStateResource.resource, () => {
      this._handleSendPostfachMailIsDone(commandStateResource);
    });
  }

  public getPendingSendPostfachMailCommand(): Observable<StateResource<CommandResource>> {
    return this.vorgangService
      .getPendingSendPostfachMailCommand()
      .pipe(map((pendingCommand) => this._pollSendPostfachMailCommand(pendingCommand)));
  }

  public getPendingResendCommand(postfachNachricht: PostfachMailResource): Observable<StateResource<CommandResource>> {
    return this.vorgangService.getAndPollPendingCommand(getUrl(postfachNachricht)).pipe(
      tapOnCommandSuccessfullyDone(() => {
        this._refreshPostfachMailList();
      }),
    );
  }

  _listenToNavigation(): void {
    this._unsubscribeToNavigation();
    this.navigationSubscription = this.navigationService.urlChanged().subscribe((params) => this._onNavigation(params));
  }

  _unsubscribeToNavigation(): void {
    if (!isNil(this.navigationSubscription)) this.navigationSubscription.unsubscribe();
  }

  _onNavigation(params: Params): void {
    if (NavigationService.isVorgangListPage(params)) {
      this._setPollingFalse();
      this.clearList();
      this._closeOpenDialogs();
      this._unsubscribe();
    }
  }

  _setPollingFalse(): void {
    this.isPollSendPostachMail.next(false);
  }

  _closeOpenDialogs(): void {
    this.dialog.closeAll();
    this.clearUploadedFiles();
  }

  _unsubscribe(): void {
    if (isNotUndefined(this.sendPostfachMailSubscription)) this.sendPostfachMailSubscription.unsubscribe();
  }

  public setPostfachMailOnReload(): void {
    this.markListAsReload();
  }

  _pollSendPostfachMailCommand(command: StateResource<CommandResource>): StateResource<CommandResource> {
    if (this.shouldPoll(command)) {
      this._setPollingTrue();
      this.sendPostfachMailSubscription = this.commandService.pollCommand(command.resource).subscribe((updatedStateResource) => {
        this.vorgangService.setPendingSendPostfachMailCommand(updatedStateResource);

        if (isDone(updatedStateResource.resource)) {
          this._handleSendPostfachMailIsDone(updatedStateResource);
          setTimeout(() => this.sendPostfachMailSubscription.unsubscribe(), 0);
        }
      });
    }
    return command;
  }

  private shouldPoll(command: StateResource<CommandResource>): boolean {
    return command.loaded && isPending(command.resource) && !this.isPollSendPostachMail.value;
  }

  _setPollingTrue(): void {
    this.isPollSendPostachMail.next(true);
  }

  _handleSendPostfachMailIsDone(updatedStateResource: StateResource<CommandResource>): void {
    this._refreshPostfachMailList();
    this._setPollingFalse();
  }

  _refreshPostfachMailList(): void {
    this.markListAsReload();
  }

  public getPostfachNachrichtListAsRead(): Observable<StateResource<PostfachMailListResource>> {
    return this.getPostfachMailListByVorgang().pipe(
      switchMap((postfachMailList: StateResource<PostfachMailListResource>) => {
        return this._resetHasNewPostfachNachrichten(postfachMailList);
      }),
      startWith(createLoadingStateResource<PostfachMailListResource>()),
    );
  }

  _resetHasNewPostfachNachrichten(
    postfachMailList: StateResource<PostfachMailListResource>,
  ): Observable<StateResource<PostfachMailListResource>> {
    if (
      isLoaded(postfachMailList) &&
      hasLink(postfachMailList.resource, PostfachMailListLinkRel.RESET_HAS_NEW_POSTFACH_NACHRICHT)
    ) {
      this._loadAfterResetHasNewPostfachNachrichten(postfachMailList.resource);
      return of(createLoadingStateResource<PostfachMailListResource>());
    }

    return of(postfachMailList);
  }

  _loadAfterResetHasNewPostfachNachrichten(postfachMailListResource: PostfachMailListResource): void {
    this.repository
      .resetHasNewPostfachNachrichten(postfachMailListResource)
      .pipe(
        take(1),
        tap(() => this.markListAsReload()),
      )
      .subscribe();
  }

  public setPostfachNachrichtListAsUnread(): void {
    this.getList()
      .pipe(
        filter(isLoaded),
        take(1),
        switchMap((postfachMailList: StateResource<PostfachMailListResource>) =>
          this.repository.markPostfachAsUnread(postfachMailList.resource),
        ),
      )
      .subscribe(() => {
        this._navigateToCurrentVorgang();
      });
  }

  _navigateToCurrentVorgang() {
    this.navigationFacade
      .getCurrentRouteData()
      .pipe(take(1))
      .subscribe((currentRoute: RouteData) => {
        this.navigationService.navigateToVorgang(VorgangNavigationUtil.getVorgangParam(currentRoute));
      });
  }

  public getPostfachMailListByVorgang(): Observable<StateResource<PostfachMailListResource>> {
    return this.getList();
  }

  public isDownloadPdfInProgress(): Observable<boolean> {
    return combineLatest([
      this.vorgangService.getVorgangWithEingang(),
      this.store.select(PostfachSelectors.isDownloadPdfInProgress),
    ]).pipe(
      tap(([vorgang, isDownloadInProgress]) => {
        if (isDownloadInProgress && vorgang.resource) {
          this._downloadPdf(vorgang.resource);
        }
      }),
      map(([, isDownloadInProgress]) => isDownloadInProgress),
    );
  }

  public downloadPdf(): void {
    this.store.dispatch(PostfachActions.startDownloadAsPdf());
  }

  _downloadPdf(vorgangWithEingang: VorgangWithEingangResource): void {
    this.store.dispatch(BinaryFileActions.downloadPdf(this.createDownloadPdfProps(vorgangWithEingang)));
  }

  createDownloadPdfProps(vorgangWithEingang: VorgangWithEingangResource): DownloadBinaryFileAsPdfAction {
    return {
      uri: getUrl(vorgangWithEingang, VorgangHeaderLinkRel.POSTFACH_MAILS),
      fileName: this.buildFileName(vorgangWithEingang),
      successAction: PostfachActions.downloadAsPdfSuccess,
      failureAction: this.createFailureAction,
    };
  }

  buildFileName(vorgangWithEingang: VorgangWithEingangResource): string {
    return `${vorgangWithEingang.nummer}_${formatFullDateWithoutSeperator(new Date())}_Nachrichten.pdf`;
  }

  createFailureAction(apiError: ApiError): ApiErrorAction & Action<string> {
    return PostfachActions.downloadAsPdfFailed({ apiError });
  }

  public getFeatures(): Observable<PostfachFeatures> {
    return this.getList().pipe(
      filter(isLoaded),
      map((listStateResource: StateResource<PostfachMailListResource>) => listStateResource.resource.features),
    );
  }

  public getSettings(): Observable<PostfachSettings> {
    return this.getList().pipe(
      filter(isLoaded),
      map((listStateResource: StateResource<PostfachMailListResource>) => listStateResource.resource.settings),
    );
  }

  public clearUploadedFiles(): void {
    this.binaryFileService.clearUploadedFiles(POSTFACH_NACHRICHT_UPLOADED_ATTACHMENTS);
  }
}
